import { apiRequest } from './apiClient';
import {
  SANDBOX_MODE,
  logSandboxPost,
  getSandboxIssuesSummary,
  getSandboxIssuesFiltered,
  updateSandboxIssue,
  addSandboxIssues,
  getSandboxSpotProducts,
} from '../config/sandbox';
import {
  PRODUCTION_MOCK,
  mockProductionByEntry,
  mockProductionByPage,
} from './productionMock';
import { PRODUCT_HISTORY_MOCK, mockProductSpotHistory } from './productHistoryMock';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export async function fetchProducts(options = {}) {
  const { limit = 50, offset = 0, search = null, brand = null, category = null, size = null } = options;

  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);
  if (brand) params.set('brand', brand);
  if (category) params.set('category', category);
  if (size) params.set('size', size);

  try {
    const response = await apiRequest(`/products?${params.toString()}`);
    const rows = response?.data || [];

    return {
      data: rows.map((row, index) => ({
        _originalIndex: offset + index,
        id: row.id ?? '',
        upc: row.upc ?? '',
        brand: row.brand ?? '',
        description: row.description ?? '',
        category: row.category ?? '',
        size: row.size ?? '',
        // form and uom are not returned by the backend SELECT — kept as empty for UI compatibility
        form: row.form ?? '',
        uom: row.uom ?? '',
      })),
      pagination: {
        total: response?.pagination?.total ?? rows.length,
        limit: response?.pagination?.limit ?? limit,
        offset: response?.pagination?.offset ?? offset,
        hasMore: response?.pagination?.hasMore ?? false,
      },
    };
  } catch (error) {
    console.error('[dataService] fetchProducts error:', error);
    return null;
  }
}

/**
 * Fetch the entire product catalog in a single gzipped request.
 * Backend returns lean fields only and skips facets — much faster than
 * paginating /products?limit=500 (~128 calls).
 */
export async function fetchAllProducts() {
  try {
    const response = await apiRequest('/products?all=true');
    const rows = response?.data || [];
    return {
      data: rows.map((row, index) => ({
        _originalIndex: index,
        id: row.id ?? '',
        upc: row.upc ?? '',
        brand: row.brand ?? '',
        description: row.description ?? '',
        category: row.category ?? '',
        size: row.size ?? '',
        form: row.form ?? '',
        uom: row.uom ?? '',
      })),
      total: response?.total ?? rows.length,
    };
  } catch (error) {
    console.error('[dataService] fetchAllProducts error:', error);
    return null;
  }
}

/**
 * Fetch autocomplete suggestions for brands, categories, or sizes.
 * @param {Object} options
 * @param {string} options.type - 'brand' | 'category' | 'size'
 * @param {string} options.q - prefix search query (optional)
 * @param {string} options.brand - filter by brand (for category/size)
 * @param {string} options.category - filter by category (for size)
 * @param {number} options.limit - max results (default 20)
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<string[]>} array of suggestions
 */
export async function fetchSuggestions(options = {}) {
  const { type, q = '', brand = '', category = '', limit = 20, signal } = options;

  const params = new URLSearchParams();
  params.set('type', type);
  if (q) params.set('q', q);
  if (brand) params.set('brand', brand);
  if (category) params.set('category', category);
  params.set('limit', String(limit));

  try {
    const response = await apiRequest(`/products/suggestions?${params.toString()}`, { signal });
    return response?.suggestions || [];
  } catch (error) {
    if (error.name === 'AbortError') {
      // Request was cancelled — not an error
      return [];
    }
    console.error('[dataService] fetchSuggestions error:', error);
    throw error;  // Let caller handle via error state
  }
}

// ---------------------------------------------------------------------------
// Spots
// ---------------------------------------------------------------------------

/**
 * Get products for one or more spots.
 * Accepts single ID: fetchSpotProducts(1485062)
 * Or array: fetchSpotProducts([1485062, 1483885])
 */
export async function fetchSpotProducts(spotIds) {
  // Sandbox seeds its own spot ids on the issue rows, so resolving them against
  // the live table would answer about unrelated spots (or 401 off a non
  // allow-listed dev origin). Serve the seed instead.
  if (SANDBOX_MODE) {
    return getSandboxSpotProducts(spotIds);
  }

  try {
    const ids = Array.isArray(spotIds) ? spotIds : [spotIds];
    const endpoint = ids.length === 1
      ? `/spots/${ids[0]}`
      : `/spots?ids=${ids.join(',')}`;

    const response = await apiRequest(endpoint);
    return {
      spots: response.spots || [],
      total_products: response.total_products || 0,
    };
  } catch (error) {
    console.error('[dataService] fetchSpotProducts error:', error);
    return null;
  }
}

/**
 * Search spots by product name/brand.
 */
export async function searchSpotsByProduct(search, limit = 50) {
  try {
    const response = await apiRequest(`/spots?search=${encodeURIComponent(search)}&limit=${limit}`);
    return {
      search: response.search,
      spots: response.spots || [],
      total: response.total || 0,
    };
  } catch (error) {
    console.error('[dataService] searchSpotsByProduct error:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Production dashboards  (mv_production_hourly / mv_shopper_page_production)
// ---------------------------------------------------------------------------

/**
 * Both endpoints return PG aggregates, which arrive as strings unless the route
 * casts them. These mappers coerce defensively so the UI never sums strings —
 * concatenating "100" + "50" into "10050" fails silently and looks like a data bug.
 */
function mapProductionEntryRow(row) {
  return {
    entryDay: row.entry_day ?? row.entryDay ?? null,
    entryHour: Number(row.entry_hour ?? row.entryHour ?? 0),
    userId: Number(row.user_id ?? row.userId ?? 0),
    firstName: row.first_name ?? row.firstName ?? '',
    customername: row.customername ?? '',
    entries: Number(row.entries ?? 0),
  };
}

function mapProductionPageRow(row) {
  return {
    customername: row.customername ?? '',
    startdate: row.startdate ?? null,
    enddate: row.enddate ?? null,
    pagenumber: Number(row.pagenumber ?? 0),
    spots: Number(row.spots ?? 0),
    firstUserId: Number(row.first_user_id ?? row.firstUserId ?? 0),
    firstName: row.first_name ?? row.firstName ?? '',
    rawMediaResource: row.raw_media_resource ?? row.rawMediaResource ?? null,
    // Clave del tool de corte. Este mapper es whitelist: un campo que no se
    // nombre aquí se pierde entre el API y el componente aunque el backend lo
    // devuelva, y el botón "Cortar spots" queda oculto sin error visible.
    publicationImageId: row.publication_image_id ?? row.publicationImageId ?? null,
  };
}

/**
 * Hour x user x retailer production rows for the "Production by Data Entry"
 * dashboard. The three vizzes are all derived client-side from this one dataset
 * (see components/production/productionRollup.js) so their totals always agree.
 *
 * @param {{ from: string, to: string, retailers?: string[], signal?: AbortSignal }} options
 * @returns {Promise<Array|null>} null on error
 */
export async function fetchProductionByEntry({ from, to, retailers = [], signal } = {}) {
  if (PRODUCTION_MOCK) {
    return mockProductionByEntry({ from, to, retailers });
  }

  try {
    const params = new URLSearchParams({ from, to });
    if (retailers.length) params.set('retailers', retailers.join(','));

    const data = await apiRequest(`/production/by-entry?${params.toString()}`, { signal });
    return (data || []).map(mapProductionEntryRow);
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('[dataService] fetchProductionByEntry error:', error);
    return null;
  }
}

/**
 * One row per circular page for the "Shoppers Processed by Page" Gantt.
 *
 * `users` filters on first_user_id — the same field that drives the bar colour —
 * so what gets filtered matches what is painted.
 *
 * @param {{ from: string, to: string, retailers?: string[], users?: number[], signal?: AbortSignal }} options
 * @returns {Promise<Array|null>} null on error
 */
export async function fetchProductionByPage({ from, to, retailers = [], users = [], signal } = {}) {
  if (PRODUCTION_MOCK) {
    return mockProductionByPage({ from, to, retailers, users });
  }

  try {
    const params = new URLSearchParams({ from, to });
    if (retailers.length) params.set('retailers', retailers.join(','));
    if (users.length) params.set('users', users.join(','));

    const data = await apiRequest(`/production/by-page?${params.toString()}`, { signal });
    return (data || []).map(mapProductionPageRow);
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('[dataService] fetchProductionByPage error:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Product spot history  (v_product_in_spot_detail)
// ---------------------------------------------------------------------------

/**
 * Whitelist mapper, same idea as the production mappers: accepts snake_case or
 * camelCase and coerces ids to numbers so `Set.has(spotId)` matches between
 * `spots[].spotId` and `products[].spotIds`.
 */
function mapSpotHistory(data, upc) {
  const toId = (v) => Number(v);
  return {
    upc: String(data?.upc ?? upc),
    description: data?.description ?? '',
    truncated: Boolean(data?.truncated),
    spots: (data?.spots || []).map((s) => ({
      spotId: toId(s.spotId ?? s.spot_id ?? s.id),
      customername: s.customername ?? '',
      startdate: s.startdate ?? null,
      pagenumber: s.pagenumber == null ? null : Number(s.pagenumber),
      promotype: s.promotype ?? '',
      adtype: s.adtype ?? '',
      spotImage: s.spotImage ?? s.spot_image ?? null,
    })),
    products: (data?.products || []).map((p) => ({
      upc: String(p.upc),
      description: p.description ?? '',
      brand: p.brand ?? '',
      size: p.size ?? '',
      spotIds: (p.spotIds ?? p.spot_ids ?? []).map(toId),
    })),
  };
}

/**
 * Spots a product appeared in (all retailers) plus every product that shared
 * those spots. Backs the "View Product History" modal in ProductsManager; the
 * retailer filter is applied client-side.
 *
 * @param {{ upc: string, from: string, to?: string, signal?: AbortSignal }} options
 * @returns {Promise<object|null>} null on error
 */
export async function fetchProductSpotHistory({ upc, from, to, signal } = {}) {
  try {
    if (PRODUCT_HISTORY_MOCK) {
      return mapSpotHistory(await mockProductSpotHistory({ upc, from, to, signal }), upc);
    }

    const params = new URLSearchParams({ from });
    if (to) params.set('to', to);

    const data = await apiRequest(
      `/products/${encodeURIComponent(upc)}/spot-history?${params.toString()}`,
      { signal }
    );
    return mapSpotHistory(data, upc);
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('[dataService] fetchProductSpotHistory error:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Issues  (swissues table)
// ---------------------------------------------------------------------------

/**
 * Fetch aggregated issue summary for dashboard cards.
 * Returns lightweight group-by-retailer-date data.
 */
function mapSummaryRow(row) {
  return {
    id: `${row.retailer}-${row.date}`,
    retailer: row.retailer,
    date: row.date,
    totalIssues: row.total_issues || 0,
    errorCount: row.error_count || 0,
    passCount: row.pass_count || 0,
    resolvedErrorCount: row.resolved_error_count || 0,
    pageErrorCount: Number(row.page_error_count) || 0,
    spotErrorCount: Number(row.spot_error_count) || 0,
    productErrorCount: Number(row.product_error_count) || 0,
    latestCreatedAt: row.latest_created_at || null,
    pageCount: row.page_count || 0,
  };
}

export async function fetchIssuesSummary(sourceArray, qcStatus = null) {
  if (SANDBOX_MODE) {
    const rows = getSandboxIssuesSummary({ sources: sourceArray, qcStatus });
    return rows.map(mapSummaryRow);
  }
  try {
    const params = new URLSearchParams({ sources: sourceArray.join(',') });
    if (qcStatus) {
      params.append('qcStatus', qcStatus);
    }
    const data = await apiRequest(`/issues/summary?${params.toString()}`);
    return (data || []).map(mapSummaryRow);
  } catch (error) {
    console.error('[dataService] fetchIssuesSummary error:', error);
    return null;
  }
}

/**
 * Fetch rows from swissues filtered by an array of source values.
 * Returns rows with display-name keys matching what IssuesResolution / QCTool expect.
 */
function mapIssueRow(row, index) {
  return {
    // Internal tracking
    _displayIndex: index,
    id: row.id,  // Database UUID
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
    userId: row.user_id ?? '',
    userEmail: row.user_email ?? '',

    // Shared
    source: row.source,
    retailer: row.retailer ?? '',
    date: row.date ?? '',
    comment: row.comment ?? '',

    // QCTool / IssuesResolution fields
    page: row.page_number ?? 0,
    shopperId: row.shopper_id ?? '',
    level: row.level ?? '',
    spotId: row.spot_id ?? '',
    upc: row.upc ?? '',
    description: row.description ?? '',
    issueType: row.issue_type ?? '',
    status: row.qc_status ?? '',
    suggestedUPC: row.suggested_upc_spot ?? '',
    observation: row.observation ?? '',

    // Meta (JSONB) and extracted shopperImage URL
    meta: row.meta ?? null,
    shopperImageUrl: row.meta?.shopperImage ?? null,

    // IssuesResolution resolution fields
    originalStatus: row.original_status ?? '',
    resolutionStatus: row.resolution_status ?? '',
    resolutionNotes: row.resolution_notes ?? '',

    // pm_new_product fields
    spotNumberLink: row.spot_number_link ?? '',
    suggestedDescription: row.suggested_description ?? '',
    referenceLink: row.reference_link ?? '',

    // Derived
    isContext: (row.level ?? '').includes('(Context)'),
  };
}

export async function fetchIssues(sourceArray, qcStatus = null, retailer = null, date = null) {
  if (SANDBOX_MODE) {
    const rows = getSandboxIssuesFiltered({ sources: sourceArray, qcStatus, retailer, date });
    return rows.map(mapIssueRow);
  }
  try {
    const params = new URLSearchParams({ sources: sourceArray.join(',') });
    if (qcStatus) params.append('qcStatus', qcStatus);
    if (retailer) params.append('retailer', retailer);
    if (date) params.append('date', date);
    const data = await apiRequest(`/issues?${params.toString()}`);
    return (data || []).map(mapIssueRow);
  } catch (error) {
    console.error('[dataService] fetchIssues error:', error);
    return null;
  }
}

/**
 * INSERT a single row into swissues.
 * Accepts a plain object whose keys match the DB snake_case column names.
 */
export async function insertIssue(row) {
  if (SANDBOX_MODE) {
    logSandboxPost('POST /issues', row);
    return { data: { inserted: [row], skipped: 0 }, error: null };
  }
  try {
    const data = await apiRequest('/issues', {
      method: 'POST',
      body: JSON.stringify(row),
    });
    return { data, error: null };
  } catch (error) {
    console.error('[dataService] insertIssue error:', error);
    return { data: null, error };
  }
}

/**
 * Bulk-insert multiple rows into swissues in a single request.
 * Uses ON CONFLICT (idempotency_key) DO NOTHING on the backend — safe to retry.
 * @returns {{ data: { inserted: Array, skipped: number } | null, error: Error | null }}
 */
export async function bulkInsertIssues(rows) {
  if (SANDBOX_MODE) {
    const result = addSandboxIssues(rows);
    logSandboxPost(
      `POST /issues/bulk  (${rows.length} row${rows.length === 1 ? '' : 's'}, ${result.inserted.length} new / ${result.skipped} skipped)`,
      { rows }
    );
    return { data: { inserted: result.inserted, skipped: result.skipped }, error: null };
  }
  try {
    const data = await apiRequest('/issues/bulk', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    });
    return { data, error: null };
  } catch (error) {
    console.error('[dataService] bulkInsertIssues error:', error);
    return { data: null, error };
  }
}

/**
 * UPDATE selected fields on a single swissues row by UUID.
 */
export async function updateIssue(id, fields) {
  if (SANDBOX_MODE) {
    const updated = updateSandboxIssue(id, fields);
    logSandboxPost(`PATCH /issues/${id}`, fields);
    return { data: updated || { id, ...fields }, error: null };
  }
  try {
    const data = await apiRequest(`/issues/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
    return { data, error: null };
  } catch (error) {
    console.error('[dataService] updateIssue error:', error);
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------------
// QC Working Data (temporary single-shopper workspace)
// ---------------------------------------------------------------------------

/**
 * Fetch current QC working data from the temp table.
 * Returns { hasData, shopper, rows } or null on error.
 */
export async function fetchQcWorkingData(options = {}) {
  const { consistent = false } = options;
  if (SANDBOX_MODE) {
    // In sandbox we don't want the QC Tool to pick up real prod working data;
    // the sandbox shopper is injected separately by QCTool.
    return { data: { hasData: false, shopper: null, rows: [] }, error: null };
  }
  try {
    // `consistent` forces this single read to hit the primary (read-after-write)
    // so a just-uploaded replace is not masked by read-replica lag. The default
    // (mount) read stays on the replica.
    const endpoint = consistent ? '/qc-working?consistent=1' : '/qc-working';
    const data = await apiRequest(endpoint);
    return { data, error: null };
  } catch (error) {
    console.error('[dataService] fetchQcWorkingData error:', error);
    return { data: null, error };
  }
}

/**
 * Upload new QC working data. Clears existing data first.
 * @param {Array} rows - Array of parsed CSV rows (camelCase keys)
 */
export async function uploadQcWorkingData(rows) {
  if (SANDBOX_MODE) {
    logSandboxPost(`POST /qc-working  (${rows.length} row${rows.length === 1 ? '' : 's'})`, { rows });
    return { data: { ok: true, count: rows.length }, error: null };
  }
  try {
    const data = await apiRequest('/qc-working', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    });
    return { data, error: null };
  } catch (error) {
    console.error('[dataService] uploadQcWorkingData error:', error);
    return { data: null, error };
  }
}

/**
 * Clear all QC working data from the temp table.
 */
export async function clearQcWorkingData() {
  if (SANDBOX_MODE) {
    logSandboxPost('DELETE /qc-working', null);
    return { error: null };
  }
  try {
    await apiRequest('/qc-working', { method: 'DELETE' });
    return { error: null };
  } catch (error) {
    console.error('[dataService] clearQcWorkingData error:', error);
    return { error };
  }
}

// ---------------------------------------------------------------------------
// Polling subscription for data refresh
// ---------------------------------------------------------------------------

/**
 * Subscribe to data changes via polling.
 *
 * @param {string} tableName     - Table name (for logging purposes)
 * @param {Function} onData      - Called with the fresh dataset whenever data changes
 * @param {Function} fetchFn     - Async function that returns fresh data (same shape as onData expects)
 * @param {number} intervalMs    - Polling interval (default 5 min)
 * @returns {Function} cleanup   - Call to clear the interval
 */
export function createSubscription(tableName, onData, fetchFn, intervalMs = REFRESH_INTERVAL_MS) {
  let lastSerialized = null;
  let pollTimer = null;

  const refresh = async () => {
    try {
      const newData = await fetchFn();
      const serialized = JSON.stringify(newData);
      if (serialized !== lastSerialized) {
        lastSerialized = serialized;
        onData(newData);
      }
    } catch (err) {
      console.error('[dataService] refresh error:', err);
    }
  };

  // Start polling immediately
  console.info(`[dataService] Starting ${intervalMs / 1000}s polling for "${tableName}"`);
  pollTimer = setInterval(refresh, intervalMs);

  return function cleanup() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
}
